import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Clock, Calendar } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { SECTION, INSIGHTS } from "@/constants/testIds";
import { fetchInsights, formatDate } from "@/lib/insightsApi";

const accentBg = (a) =>
  a === "gold"
    ? "from-amber-100 via-amber-50 to-white"
    : a === "navy"
    ? "from-slate-100 via-slate-50 to-white"
    : "from-cyan-100 via-cyan-50 to-white";

export default function InsightsSection() {
  const { t, lang } = useI18n();
  const i = t.insights;
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetchInsights()
      .then((list) => mounted && setArticles(list.slice(0, 3)))
      .catch(() => mounted && setArticles([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section
      id="insights"
      data-testid={SECTION.insights}
      className="section-y bg-white relative"
    >
      <div className="container-x">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="text-overline">{i.overline}</span>
            <h2 className="mt-4 font-display font-semibold text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
              {i.title}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{i.paragraph}</p>
          </div>
          <Link
            to="/insights"
            data-testid={INSIGHTS.viewAll}
            className="btn-outline self-start lg:self-end whitespace-nowrap"
          >
            {i.viewAll} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {loading
            ? Array.from({ length: 3 }).map((_, k) => (
                <div
                  key={k}
                  className="h-72 rounded-2xl border border-slate-200 bg-slate-50 animate-pulse"
                />
              ))
            : articles.map((a, k) => (
                <motion.div
                  key={a.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: k * 0.08 }}
                >
                  <Link
                    to={`/insights/${a.slug}`}
                    data-testid={`${INSIGHTS.card}-${a.slug}`}
                    className="group block h-full rounded-2xl border border-slate-200 bg-white hover:shadow-xl hover:border-navy/30 transition-all overflow-hidden"
                  >
                    <div
                      className={`aspect-[16/9] bg-gradient-to-br ${accentBg(
                        a.accent
                      )} relative`}
                    >
                      <div className="absolute inset-0 grid-bg opacity-50" />
                      <span className="absolute top-4 left-4 inline-flex items-center px-2.5 py-1 rounded-full bg-white/85 backdrop-blur text-[11px] font-semibold uppercase tracking-[0.18em] text-navy border border-slate-200">
                        {a.tag[lang]}
                      </span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(a.date, lang)}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          {a.reading_minutes} {i.minRead}
                        </span>
                      </div>
                      <h3 className="mt-4 font-display text-xl font-semibold text-navy leading-snug group-hover:text-cyan-700 transition-colors">
                        {a.titles[lang]}
                      </h3>
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
                        {a.excerpts[lang]}
                      </p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-navy group-hover:gap-2.5 transition-all">
                        {i.readMore} <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
        </div>
      </div>
    </section>
  );
}
